/**
 * Region Layout Utilities
 * 
 * Functions for calculating region positioning, spanning, and pagination in the grid.
 * These handle the complex logic of regions that span multiple lines and pages.
 */

import { BARS_PER_LINE, BARS_PER_PAGE } from '../constants/grid';
import { ARROW_TYPES } from '../constants/regions';

/**
 * Check if a region spans multiple lines
 * 
 * @param {number} startBar - Start bar of region
 * @param {number} endBar - End bar of region (exclusive)
 * @returns {boolean} True if region spans multiple lines
 */
export function isMultiLineRegion(startBar, endBar) {
  const startLine = Math.floor(startBar / BARS_PER_LINE);
  const endLine = Math.floor((endBar - 1) / BARS_PER_LINE);
  
  return startLine !== endLine;
}

/**
 * Check if a region crosses a page boundary
 * 
 * @param {number} barPosition - Current bar position
 * @param {number} currentPage - Current page number (0-indexed)
 * @returns {boolean} True if bar crosses to next page
 */
export function crossesPageBoundary(barPosition, currentPage) {
  return Math.floor(barPosition / BARS_PER_PAGE) > currentPage;
}

/**
 * Calculate grid column start position for a bar
 * Column 1 is padding, columns 2-9 are the bars, column 10 is padding
 * 
 * @param {number} barNumber - Bar number within the line
 * @returns {number} CSS Grid column start (1-indexed)
 */
export function getColumnStart(barNumber) {
  return (barNumber % BARS_PER_LINE) + 2;
}

/**
 * Calculate grid column end position for a region
 * 
 * @param {number} startBar - Start bar position
 * @param {number} endBar - End bar position (exclusive)
 * @param {boolean} isLineContinuation - Whether this is a continuation from previous line
 * @returns {number|string} CSS Grid column end value
 */
export function getColumnEnd(startBar, endBar, isLineContinuation = false) {
  if (isLineContinuation) {
    // For line continuation, calculate the actual end column position
    const endBarPosition = endBar % BARS_PER_LINE;
    
    // If the segment extends to the end of the line
    if (endBarPosition === 0) {
      return BARS_PER_LINE + 2;
    }
    
    // Return the specific column where the region should end
    return endBarPosition + 2;
  }
  
  const barsInRegion = endBar - startBar;
  
  // Check if region extends to end of line
  if (endBar % BARS_PER_LINE === 0) {
    return BARS_PER_LINE + 2;
  }
  
  return `span ${barsInRegion}`;
}

/**
 * Determine arrow type for a region segment
 * 
 * @param {boolean} hasLeftContinuation - Region continues from previous line
 * @param {boolean} hasRightContinuation - Region continues to next line
 * @returns {string|null} Arrow type constant
 */
export function getArrowType(hasLeftContinuation, hasRightContinuation) {
  if (hasLeftContinuation && hasRightContinuation) {
    return ARROW_TYPES.BOTH;
  }
  if (hasRightContinuation) {
    return ARROW_TYPES.RIGHT;
  }
  if (hasLeftContinuation) {
    return ARROW_TYPES.LEFT;
  }
  return ARROW_TYPES.NONE;
}

/**
 * Split a multi-line region into individual line segments
 * 
 * @param {object} region - Region data with start and end bars
 * @returns {Array<object>} Array of region segments with positioning info
 * 
 * @example
 * // Region spanning bars 6-18 (3 lines)
 * splitRegionIntoLines({ start: 6, end: 18, type: 'rhythm', ... })
 * // Returns: [
 * //   { ...region, lineStart: 6, lineEnd: 8, arrow: 'r' },
 * //   { ...region, lineStart: 8, lineEnd: 16, arrow: 'rl' },
 * //   { ...region, lineStart: 16, lineEnd: 18, arrow: 'l' }
 * // ]
 */
export function splitRegionIntoLines(region) {
  const { start, end } = region;
  
  if (!isMultiLineRegion(start, end)) {
    const lineNumber = Math.floor(start / BARS_PER_LINE);
    return [{ ...region, lineStart: start, lineEnd: end, arrow: null, lineNumber }];
  }
  
  const segments = [];
  const startLine = Math.floor(start / BARS_PER_LINE);
  const endLine = Math.floor((end - 1) / BARS_PER_LINE);
  
  for (let line = startLine; line <= endLine; line++) {
    const lineStartBar = Math.max(start, line * BARS_PER_LINE);
    const lineEndBar = Math.min(end, (line + 1) * BARS_PER_LINE);
    
    const hasLeftContinuation = line > startLine;
    const hasRightContinuation = line < endLine;
    const arrow = getArrowType(hasLeftContinuation, hasRightContinuation);
    
    segments.push({
      ...region,
      lineStart: lineStartBar,
      lineEnd: lineEndBar,
      arrow,
      lineNumber: line,
    });
  }
  
  return segments;
}

/**
 * Group regions into pages based on bar positions
 * 
 * @param {Array<object>} regions - Array of region objects with start/end bars
 * @returns {Array<Array<object>>} 2D array of regions grouped by page
 */
export function groupRegionsByPage(regions) {
  const pages = [];
  let currentPage = [];
  let currentPageNumber = 0;
  
  regions.forEach(region => {
    const segments = splitRegionIntoLines(region);
    
    segments.forEach(segment => {
      const segmentPageNumber = Math.floor(segment.lineStart / BARS_PER_PAGE);
      
      // Start new page if segment belongs to next page
      if (segmentPageNumber > currentPageNumber) {
        if (currentPage.length > 0) {
          pages.push(currentPage);
        }
        currentPage = [];
        currentPageNumber = segmentPageNumber;
      }
      
      currentPage.push(segment);
    });
  });
  
  // Add final page
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }
  
  return pages;
}

/**
 * Calculate total number of pages needed for regions
 * 
 * @param {Array<object>} regions - Array of region objects
 * @returns {number} Total page count
 */
export function calculateTotalPages(regions) {
  if (regions.length === 0) return 1;
  
  const lastRegion = regions[regions.length - 1];
  const lastBar = lastRegion.end;
  
  return Math.floor(lastBar / BARS_PER_PAGE) + 1;
}

/**
 * Generate bar numbers for display on a given page
 * Shows numbers at every 4th bar starting from bar 1
 * 
 * @param {number} pageNumber - Page number (1-indexed)
 * @returns {Array<{position: number, number: number|null, isLight: boolean}>}
 */
export function generateBarNumbers(pageNumber) {
  const numbers = [];
  const startBar = (pageNumber - 1) * BARS_PER_PAGE;
  const endBar = pageNumber * BARS_PER_PAGE;
  
  // Add padding column
  numbers.push({ position: 0, number: null, isLight: false });
  
  for (let bar = startBar; bar < endBar; bar++) {
    const isLineStart = bar % BARS_PER_LINE === 0;
    const shouldShowNumber = bar % 4 === 0 && !isLineStart;
    
    if (isLineStart) {
      // Line start positions show as empty
      numbers.push({ position: bar, number: null, isLight: false });
    } else if (shouldShowNumber) {
      // Show bar number (1-indexed for display)
      numbers.push({ position: bar, number: bar + 1, isLight: false });
    } else {
      // Empty position
      numbers.push({ position: bar, number: null, isLight: false });
    }
  }
  
  return numbers;
}


